import { forwardRef } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * AdminSelect — single source of truth for native <select> styling in the admin panel.
 *
 * Handles dark-theme tokens, focus/hover/disabled states, and the dropdown chevron.
 * Supports three sizes via the `size` prop:
 *   "sm"      — text-[10px], compact padding  (template slot selects)
 *   "default" — text-xs, standard padding      (most admin selects)
 *   "md"      — text-xs, slightly larger padding (form fields)
 *
 * Usage:
 *   <AdminSelect value={v} onChange={fn} data-testid="my-select">
 *     <option value="">— Pick —</option>
 *     <option value="a">A</option>
 *   </AdminSelect>
 */

const SIZE_CLASSES = {
  sm: "text-[10px] px-2 py-1.5",
  default: "text-xs px-2.5 py-1.5",
  md: "text-xs px-3 py-2.5",
};

const AdminSelect = forwardRef(
  ({ className, size = "default", children, ...props }, ref) => (
    <div className={cn("relative", className)}>
      <select
        ref={ref}
        className={cn(
          /* base */
          "w-full appearance-none rounded outline-none transition-colors",
          /* dark theme — explicit, not dependent on Tailwind opacity hacks */
          "bg-[#0a0a0a] border border-white/10 text-white",
          /* focus / hover */
          "hover:border-white/20 focus:border-[#CBA052]/40 focus:ring-1 focus:ring-[#CBA052]/20",
          /* disabled */
          "disabled:opacity-40 disabled:cursor-not-allowed",
          /* chevron space */
          "pr-7",
          /* size variant */
          SIZE_CLASSES[size] || SIZE_CLASSES.default,
        )}
        {...props}
      >
        {children}
      </select>
      <ChevronDown
        size={size === "sm" ? 10 : 12}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none"
      />
    </div>
  ),
);

AdminSelect.displayName = "AdminSelect";

export { AdminSelect };
